import { useEffect, useRef, useState } from 'react';
import { loadConfig, siteName as knownSiteName } from '@harmony/shared/config';
import { LEGAL_PRIVACY_PATH, LEGAL_TERMS_PATH } from '@harmony/shared/legal';
import iconUrl from '@harmony/theme/brand/icon.svg';
import crecUrl from '@harmony/theme/brand/crec.svg';
import { CathedralWindow } from './CathedralWindow';

/** The CREC's own site, linked from the footer. */
const CREC_URL = 'https://crechurches.org';

/**
 * The public landing page.
 *
 * Everything a signed-out stranger is offered: what this is, a way in for
 * somebody who already has an account, and -- for somebody who does not -- the
 * one true answer, which is to ask their minister. There is no sign-up here and
 * there is not going to be one: an invitation from inside a church is the only
 * way onto the platform (FUNCTIONAL-SPEC, and README's guiding principles).
 */
export function App() {
  const [siteName, setSiteName] = useState(knownSiteName);
  const [supportEmail, setSupportEmail] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    /*
     * The config is the ONLY thing this page fetches, and it must not be able
     * to break it: the name falls back to what is already on screen and the
     * support line simply stays absent. A landing page that renders an error
     * because a JSON file was slow is worse than one missing an address.
     */
    loadConfig()
      .then((config) => {
        if (config.siteName) {
          setSiteName(config.siteName);
          // The tab title too: index.html carries a literal so there is
          // something before any script runs, and this is what makes it follow
          // HARMONY_SITE_NAME afterwards.
          document.title = config.siteName;
        }
        if (config.supportEmail) setSupportEmail(config.supportEmail);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="page">
      <header className="hero">
        <nav className="nav">
          <a className="brand" href="/">
            <img className="brandMark" src={iconUrl} alt="" />
            <span className="brandName">{siteName}</span>
          </a>
          <a className="loginPill" href="/app/">
            Log In
          </a>
        </nav>

        <div className="heroBody">
          <div className="heroText">
            <p className="eyebrow">✦&ensp;One flesh&ensp;·&ensp;One faith</p>
            <h1 className="verse">
              An excellent wife who can find? She is far more precious than jewels.
              <span className="cite">Proverbs 31:10</span>
            </h1>
            <p className="lede">
              A service connecting CREC singles who are aiming to form a joyful, Christ-centered home.
            </p>
            <div className="heroActions">
              <button
                className="btnLight"
                type="button"
                onClick={() => dialog.current?.showModal()}
              >
                Request an Invitation
              </button>
              {/*
                The same button shape as the one above it, with a chevron: on a
                phone the two sit in a centered column and a link dressed as a
                line of text reads as a caption rather than as the second way
                on. The chevron is what says this one LEAVES the page.
              */}
              <a className="btnLight btnAway" href="/articles/">
                Articles on Courtship &amp; Marriage
                <svg className="btnChevron" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M9 5l7 7-7 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </div>
          </div>

          <div className="heroArt">
            <CathedralWindow />
          </div>
        </div>
      </header>

      <footer className="footer">
        <p className="footerLead">
          {/* The crest, at the size of the line it sits on -- decoration beside
              a link that already says whose it is, so it carries no alt text. */}
          <a href={CREC_URL} rel="noreferrer">
            <img className="crecMark" src={crecUrl} alt="" />A ministry for the CREC
          </a>
        </p>
        {supportEmail && (
          <p className="footerNote">
            Having trouble? Email <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
          </p>
        )}
        {/*
          The two documents, on the one page a stranger can reach without an
          account. The Policy in particular has to be readable BEFORE
          registration, because it describes collection that begins at it.

          Ordinary links, and no `target="_blank"`: this page is not an
          installed app and has nothing in progress to protect -- unlike the
          registration form, where opening the Terms in place would sit on top
          of half-filled fields. Here it is a page giving way to a page.
        */}
        <p className="footerNote footerLegal">
          <a href={LEGAL_TERMS_PATH}>Terms of Service</a>
          <span aria-hidden="true"> · </span>
          <a href={LEGAL_PRIVACY_PATH}>Privacy Policy</a>
        </p>
      </footer>

      {/*
        A native <dialog>: modality, focus containment and Escape are the
        browser's, which is a great deal of behaviour not to have to write --
        and this page carries no component kit to borrow it from.

        A dialog rather than a section further down the page, because there is
        nothing further down the page: the answer is one sentence about a
        conversation to have offline, and it does not need a destination.
      */}
      <dialog className="modal" ref={dialog}>
        <h3 className="modalTitle">Ask your minister</h3>
        <p>
          {siteName} is by invitation only. Talk to the minister at your CREC church about inviting
          you onto the platform.
        </p>
        {/*
          The whole sentence, not just the address. "He is welcome to write to
          us." with nowhere to write is worse than saying nothing -- and there
          is genuinely nothing to say when no support mailbox is configured.
        */}
        {supportEmail && (
          <p>
            If your church is not here yet, he is welcome to write to us at{' '}
            <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
          </p>
        )}
        <form method="dialog">
          {/*
            Named explicitly, or the browser opens the dialog with the support
            address focused -- the first focusable thing in it -- and the ring
            round a mailto link reads as the thing to press.
          */}
          <button className="btnSolid" type="submit" autoFocus>
            Close
          </button>
        </form>
      </dialog>
    </div>
  );
}
